<script setup lang="ts">
/**
 * Outfit quiz "Outfit for an occasion" — full-screen layer (Figma Quizzes › Outfit quiz ·
 * desktop 3229:18076 / mobile 3229:104944). An alternative entry to the Playground: three
 * outfits are generated per request (R133) and Open outfits continues on the Playground's
 * pick-one screen with these three. Answers are session-only, so × asks first (11).
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useFairCompact } from '@/composables/useFairCompact'
import { postAiraAction } from '@/features/aira/api/aira'
import type { AiraProposedOutfit } from '@/features/aira/api/types'
import { useUpdatePersonalDataMutation } from '@/mutations/profile'
import { useUserProfileDataQuery } from '@/queries/profile/useUserProfileDataQuery'
import {
  FairQuizGlassPill,
  FairQuizHeader,
  FairQuizLayer,
  FairQuizLeaveDialog,
  FairQuizStep,
  FairQuizWelcome,
  settleFairQuizHistory,
} from '@/ui/patterns/FairQuiz'
import { useFairToast } from '@/ui/patterns/FairToast'
import { FairChip } from '@/ui/primitives/FairChip'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairInputText } from '@/ui/primitives/FairInputText'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'
import { FairThemeCard } from '@/ui/primitives/FairThemeCard'
import { FairToggleItem } from '@/ui/primitives/FairToggleItem'
import { readStoredCurrency } from '@/ui/tokens/fair-locale-menu.data'
import { buildFairStyleSpaceChooseLocation } from '@/widgets/style-space/fair-v2/fair-style-space-v2-routes.data'
import { useOutfitQuizStore } from '../store'
import {
  OUTFIT_QUIZ_BUDGETS,
  OUTFIT_QUIZ_DRESS_CODES,
  OUTFIT_QUIZ_OCCASIONS,
  OUTFIT_QUIZ_WEATHER,
  emptyOutfitQuizAnswers,
  formatOutfitQuizBudget,
  outfitQuizActionBody,
  outfitQuizAudience,
  outfitQuizProgress,
  outfitQuizProposed,
  outfitQuizSteps,
  toggleOutfitQuizChoice,
  type OutfitQuizAnswers,
  type OutfitQuizStep,
} from '../outfit-quiz.data'
import OutfitQuizLook from './OutfitQuizLook.vue'
import './outfit-quiz.css'

const store = useOutfitQuizStore()
const { t, locale } = useI18n()
const router = useRouter()
const toast = useFairToast()
const compact = useFairCompact()
const profileQuery = useUserProfileDataQuery(computed(() => store.isOpen))
const personalDataMutation = useUpdatePersonalDataMutation()

const step = ref<OutfitQuizStep>('welcome')
const askGender = ref(false)
const gender = ref<'woman' | 'man' | null>(null)
const steps = computed(() => outfitQuizSteps(askGender.value))
const answers = ref<OutfitQuizAnswers>(emptyOutfitQuizAnswers())
const currency = ref('USD')
const saving = ref(false)

function move(by: 1 | -1) {
  const index = steps.value.indexOf(step.value)
  const next = steps.value[Math.max(0, Math.min(steps.value.length - 1, index + by))]
  if (next) step.value = next
}

/* ── Gender (01) ──────────────────────────────────────────────────────── */

async function continueGender() {
  if (!gender.value) return
  saving.value = true
  try {
    await personalDataMutation.mutateAsync({ gender: gender.value })
    move(1)
  } catch (error) {
    console.error('Outfit quiz: gender not saved', error)
    toast.show({ tone: 'warning', message: t('outfit_quiz.save_error') })
  } finally {
    saving.value = false
  }
}

/* ── Labels ───────────────────────────────────────────────────────────── */

const budgetLabels = computed(() => ({ under: t('outfit_quiz.budget.under'), notSure: t('outfit_quiz.budget.not_sure') }))

const occasionLabel = computed(() => {
  const value = answers.value.occasion
  if (!value) return ''
  if (value === 'other' && answers.value.occasionText.trim()) return answers.value.occasionText.trim()
  return t(`outfit_quiz.occasion.options.${value}`)
})

/** "Three outfits for a wedding guest" / Other: the typed words. */
const occasionFor = computed(() => {
  const value = answers.value.occasion
  if (!value) return ''
  if (value === 'other' && answers.value.occasionText.trim()) return answers.value.occasionText.trim()
  return t(`outfit_quiz.occasion.for.${value}`)
})

const dressCodeLabel = computed(() => {
  const value = answers.value.dressCode
  if (!value) return ''
  return value === 'custom' ? answers.value.dressCodeText.trim() : t(`outfit_quiz.details.dress_codes.${value}`)
})

const weatherLabel = computed(() => {
  const value = answers.value.weather
  if (!value || value === 'not_sure') return ''
  return value === 'custom' ? answers.value.weatherText.trim() : t(`outfit_quiz.details.weathers.${value}`)
})

const budgetLabel = computed(() =>
  answers.value.budget && answers.value.budget !== 'not_sure'
    ? formatOutfitQuizBudget(answers.value.budget, currency.value, budgetLabels.value)
    : '',
)

/** 09: occasion, dress code, weather, budget. */
const chips = computed(() =>
  [occasionLabel.value, dressCodeLabel.value, weatherLabel.value, budgetLabel.value].filter(Boolean),
)

/* ── Details (04 / 05): Custom opens a field and clears the rest (R171) ─── */

function chooseDressCode(value: (typeof OUTFIT_QUIZ_DRESS_CODES)[number]) {
  answers.value = { ...answers.value, dressCode: toggleOutfitQuizChoice(answers.value.dressCode, value) }
  if (answers.value.dressCode !== 'custom') answers.value.dressCodeText = ''
}

function chooseWeather(value: (typeof OUTFIT_QUIZ_WEATHER)[number]) {
  answers.value = { ...answers.value, weather: toggleOutfitQuizChoice(answers.value.weather, value) }
  if (answers.value.weather !== 'custom') answers.value.weatherText = ''
}

/* ── Generation ───────────────────────────────────────────────────────── */

const outfits = ref<AiraProposedOutfit[]>([])
let generation = 0

async function createOutfits() {
  const ticket = ++generation
  step.value = 'loading'
  try {
    const { data } = await postAiraAction(
      outfitQuizActionBody(
        answers.value,
        { occasion: occasionLabel.value, dressCode: dressCodeLabel.value, weather: weatherLabel.value, budget: budgetLabel.value },
        outfitQuizAudience(gender.value ?? profileQuery.data.value?.user?.gender),
        String(locale.value || 'en'),
        crypto.randomUUID(),
      ),
    )
    if (ticket !== generation) return
    const proposed = outfitQuizProposed(data)
    if (!proposed) {
      step.value = 'error'
      return
    }
    outfits.value = proposed.slice(0, 3)
    step.value = 'result'
  } catch (error) {
    if (ticket !== generation) return
    console.error('Outfit quiz: outfits did not load', error)
    step.value = 'error'
  }
}

/** × on Loading cancels (08): the answer is dropped when it comes. */
function cancelGeneration() {
  generation += 1
  step.value = 'note'
}

async function openOutfits() {
  const choose = buildFairStyleSpaceChooseLocation({
    messageKey: `outfit-quiz-${Date.now()}`,
    occasion: null,
    occasionLabel: occasionLabel.value || null,
    outfits: outfits.value,
  })
  store.close()
  await settleFairQuizHistory()
  await router.push(choose)
}

/* ── Header + leaving ─────────────────────────────────────────────────── */

const progress = computed(() => outfitQuizProgress(steps.value, step.value))
const headerRight = computed(() => (['details', 'budget', 'note'].includes(step.value) ? 'skip' : 'none'))
const leaveOpen = ref(false)

function requestClose() {
  if (step.value === 'loading') {
    cancelGeneration()
    return
  }
  if (['welcome', 'result', 'error'].includes(step.value)) {
    store.close()
    return
  }
  leaveOpen.value = true
}

function exit() {
  leaveOpen.value = false
  store.close()
}

function skip() {
  if (step.value === 'note') {
    void createOutfits()
    return
  }
  move(1)
}

/* ── Open ─────────────────────────────────────────────────────────────── */

watch(
  () => store.session,
  async () => {
    step.value = 'welcome'
    answers.value = emptyOutfitQuizAnswers()
    outfits.value = []
    currency.value = readStoredCurrency()
    await profileQuery.refresh(true)
    const known = outfitQuizAudience(profileQuery.data.value?.user?.gender)
    askGender.value = !known
    gender.value = null
  },
  { immediate: true },
)
</script>

<template>
  <FairQuizLayer
    :open="store.isOpen"
    class="outfit-quiz"
    :class="`outfit-quiz--${step}`"
    :aria-label="t('outfit_quiz.aria')"
    @close-request="requestClose"
  >
    <FairQuizHeader
      :progress="progress"
      :right="headerRight"
      :skip-label="t('quiz.skip')"
      :close-label="t('quiz.close')"
      :progress-label="t('quiz.progress')"
      @close="requestClose"
      @skip="skip"
    />

    <FairQuizWelcome
      v-if="step === 'welcome'"
      :title="t('outfit_quiz.welcome.title')"
      :subtitle="t('outfit_quiz.welcome.subtitle')"
      :pills="[{ label: t('outfit_quiz.welcome.pill_time'), star: false }]"
      :continue-label="t('quiz.continue')"
      @continue="move(1)"
    />

    <!-- 01 Gender -->
    <FairQuizStep
      v-else-if="step === 'gender'"
      :title="t('outfit_quiz.gender.title')"
      :subtitle="t('outfit_quiz.gender.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!gender"
      :continue-loading="saving"
      @back="move(-1)"
      @continue="continueGender"
    >
      <div
        class="outfit-quiz__gender"
        role="radiogroup"
        :aria-label="t('outfit_quiz.gender.title')"
      >
        <FairToggleItem
          v-for="option in (['woman', 'man'] as const)"
          :key="option"
          :label="t(`outfit_quiz.gender.${option}`)"
          :selected="gender === option"
          stretch
          large
          @click="gender = option"
        />
      </div>
    </FairQuizStep>

    <!-- 02 / 03 Occasion -->
    <FairQuizStep
      v-else-if="step === 'occasion'"
      fill
      :title="t('outfit_quiz.occasion.title')"
      :subtitle="t('outfit_quiz.occasion.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!answers.occasion"
      @back="move(-1)"
      @continue="move(1)"
    >
      <div class="outfit-quiz__content">
        <div
          class="outfit-quiz__cards"
          role="radiogroup"
          :aria-label="t('outfit_quiz.occasion.title')"
        >
          <FairThemeCard
            v-for="option in OUTFIT_QUIZ_OCCASIONS"
            :key="option.value"
            :label="t(`outfit_quiz.occasion.options.${option.value}`)"
            :icon="option.icon"
            :selected="answers.occasion === option.value"
            @click="answers = { ...answers, occasion: option.value, occasionText: option.value === 'other' ? answers.occasionText : '' }"
          />
        </div>
        <FairInputText
          v-if="answers.occasion === 'other'"
          v-model="answers.occasionText"
          variant="underline"
          :placeholder="t('outfit_quiz.occasion.custom')"
        />
      </div>
    </FairQuizStep>

    <!-- 04 / 05 Details -->
    <FairQuizStep
      v-else-if="step === 'details'"
      fill
      :title="t('outfit_quiz.details.title')"
      :subtitle="t('outfit_quiz.details.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      @back="move(-1)"
      @continue="move(1)"
    >
      <div class="outfit-quiz__details">
        <section class="outfit-quiz__block">
          <h3 class="outfit-quiz__label">
            {{ t('outfit_quiz.details.dress_code') }}
          </h3>
          <div
            class="outfit-quiz__toggles"
            role="radiogroup"
            :aria-label="t('outfit_quiz.details.dress_code')"
          >
            <FairToggleItem
              v-for="option in OUTFIT_QUIZ_DRESS_CODES"
              :key="option"
              :label="t(`outfit_quiz.details.dress_codes.${option}`)"
              :selected="answers.dressCode === option"
              @click="chooseDressCode(option)"
            >
              <FairIcon
                v-if="option === 'custom'"
                name="ai"
                :size="12"
              />
              {{ t(`outfit_quiz.details.dress_codes.${option}`) }}
            </FairToggleItem>
          </div>
          <FairInputText
            v-if="answers.dressCode === 'custom'"
            v-model="answers.dressCodeText"
            variant="underline"
            :placeholder="t('outfit_quiz.details.dress_code')"
          />
        </section>
        <section class="outfit-quiz__block">
          <h3 class="outfit-quiz__label">
            {{ t('outfit_quiz.details.weather') }}
          </h3>
          <div
            class="outfit-quiz__toggles"
            role="radiogroup"
            :aria-label="t('outfit_quiz.details.weather')"
          >
            <FairToggleItem
              v-for="option in OUTFIT_QUIZ_WEATHER"
              :key="option"
              :label="t(`outfit_quiz.details.weathers.${option}`)"
              :selected="answers.weather === option"
              @click="chooseWeather(option)"
            >
              <FairIcon
                v-if="option === 'custom'"
                name="ai"
                :size="12"
              />
              {{ t(`outfit_quiz.details.weathers.${option}`) }}
            </FairToggleItem>
          </div>
          <FairInputText
            v-if="answers.weather === 'custom'"
            v-model="answers.weatherText"
            variant="underline"
            :placeholder="t('outfit_quiz.details.weather')"
          />
        </section>
      </div>
    </FairQuizStep>

    <!-- 06 Budget -->
    <FairQuizStep
      v-else-if="step === 'budget'"
      fill
      :title="t('outfit_quiz.budget.title')"
      :subtitle="t('outfit_quiz.budget.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      @back="move(-1)"
      @continue="move(1)"
    >
      <div
        class="outfit-quiz__cards"
        role="radiogroup"
        :aria-label="t('outfit_quiz.budget.title')"
      >
        <FairThemeCard
          v-for="option in OUTFIT_QUIZ_BUDGETS"
          :key="option.value"
          :label="formatOutfitQuizBudget(option.value, currency, budgetLabels)"
          :selected="answers.budget === option.value"
          @click="answers = { ...answers, budget: toggleOutfitQuizChoice(answers.budget, option.value) }"
        />
      </div>
    </FairQuizStep>

    <!-- 07 Note -->
    <FairQuizStep
      v-else-if="step === 'note'"
      fill
      :title="t('outfit_quiz.note.title')"
      :subtitle="t('outfit_quiz.note.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('outfit_quiz.note.create')"
      @back="move(-1)"
      @continue="createOutfits"
    >
      <FairInputText
        v-model="answers.note"
        variant="underline"
        :placeholder="t('outfit_quiz.note.placeholder')"
      />
    </FairQuizStep>

    <!-- 08 Loading -->
    <FairQuizStep
      v-else-if="step === 'loading'"
      fill
      :title="t('outfit_quiz.loading.title')"
      :subtitle="t('outfit_quiz.loading.subtitle', { occasion: occasionFor })"
      :show-footer="false"
    >
      <div
        class="outfit-quiz__skeleton"
        aria-busy="true"
      >
        <FairSkeleton
          v-for="index in 3"
          :key="index"
          class="outfit-quiz__skeleton-card"
          variant="media"
          :mark="false"
        />
      </div>
    </FairQuizStep>

    <!-- 09 Result: no Back — this is a result -->
    <FairQuizStep
      v-else-if="step === 'result'"
      fill
      class="outfit-quiz__result"
      :title="t('outfit_quiz.result.title', { occasion: occasionFor })"
      :subtitle="t('outfit_quiz.result.subtitle')"
      :show-back="false"
      :continue-label="t('outfit_quiz.result.open')"
      @continue="openOutfits"
    >
      <template #eyebrow>
        <div class="outfit-quiz__tag">
          <FairQuizGlassPill :label="t('outfit_quiz.result.pill')" />
        </div>
      </template>
      <div class="outfit-quiz__result-content">
        <div class="outfit-quiz__chips">
          <FairChip
            v-for="chip in chips"
            :key="chip"
            :label="chip"
            :size="compact ? 'mobile' : 'default'"
          />
        </div>
        <div class="outfit-quiz__looks">
          <OutfitQuizLook
            v-for="(outfit, index) in outfits"
            :key="index"
            :items="outfit.items"
            :label="outfit.rationale || t('outfit_quiz.result.title', { occasion: occasionFor })"
          />
        </div>
      </div>
    </FairQuizStep>

    <!-- 10 Error: answers kept, no attempt is used (R168) -->
    <FairQuizStep
      v-else-if="step === 'error'"
      :title="t('outfit_quiz.error.title')"
      :subtitle="t('outfit_quiz.error.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('outfit_quiz.error.retry')"
      @back="step = 'note'"
      @continue="createOutfits"
    />

    <FairQuizLeaveDialog
      v-model:visible="leaveOpen"
      :title="t('outfit_quiz.leave.title')"
      :text="t('outfit_quiz.leave.text')"
      :keep-label="t('outfit_quiz.leave.keep')"
      :exit-label="t('outfit_quiz.leave.exit')"
      :close-label="t('quiz.close')"
      @exit="exit"
    />
  </FairQuizLayer>
</template>
